import {SPECS} from './rx-design-specs.js?v=20260909-menu-ribbon-1';
export {SPECS};
import {catalogue, loadPages, groupsFor, choiceSummary, editionRibbon, editionChoices} from './rx-design-registry.js?v=20260909-menu-ribbon-1';
export const garmentLabel = garment => ({tee:'T-shirt',hoodie:'Hoodie',jacket:'Jacket'}[garment] || garment);

export function matchesDesign(design, filters) {
  return (!filters.series || design.universe===filters.series) && (!filters.garment || design.slots.some(s=>SPECS[s.key].garment===filters.garment)) && `${design.title} ${design.universe} ${design.line || ''}`.toLowerCase().includes((filters.query || '').trim().toLowerCase());
}

export function optionNames(product) {
  return (product.options || []).map(option => typeof option === 'string' ? option : option.name);
}

export function optionValues(product, index) {
  return [...new Set(product.variants.map(variant => variant.options[index]))];
}

export function matchingVariant(product, choices) {
  const names = optionNames(product);
  if (!names.every(name => choices[name] !== undefined)) return null;
  return product.variants.find(variant => names.every((name, index) => variant.options[index] === choices[name])) || null;
}

export function optionAvailable(product, choices, index, value) {
  const names = optionNames(product);
  return product.variants.some(variant => variant.available && variant.options[index] === value && names.every((name, other) => {
    // Colour choices must be switchable even when the previous size is unavailable.
    return other >= index || !choices[name] || variant.options[other] === choices[name];
  }));
}

export function compatibleChoices(product, previous = {}) {
  const result = {};
  optionNames(product).forEach((name, index) => {
    if (previous[name] && optionAvailable(product, result, index, previous[name])) result[name] = previous[name];
    else if (/colou?r/i.test(name)) {
      const values = optionValues(product, index).filter(value => optionAvailable(product, result, index, value));
      result[name] = values.find(value => /black/i.test(value)) || values[0];
    } else if (optionValues(product, index).length === 1 && !/size/i.test(name)) {
      result[name] = optionValues(product, index)[0];
    }
  });
  return result;
}

export function safeImage(value) {
  if (!value) return '';
  const src = typeof value === 'string' ? value : value.src || value.url;
  if (typeof src !== 'string') return '';
  if (src.startsWith('//')) return 'https:' + src;
  return /^(https?:\/\/|\/(?!\/)|\.\/assets\/)/i.test(src) ? src : '';
}

const escapeHTML = value => String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));

class DesignShop {
  constructor(root) {
    this.root = root;
    this.data = JSON.parse(root.querySelector('[data-design-data]').textContent);
    this.view = root.querySelector('[data-design-view]');
    this.state = { design: null, slot: null, product: null, choices: {}, image: '', loading: false, error: '', busy: false };
    this.cache = new Map();
    this.request = 0;
    this.cartBusy = false;
    this.filters = {query:'',series:'',garment:''};
    this.root.addEventListener('click', event => this.click(event));
    this.root.addEventListener('submit', event => this.submit(event));
    this.root.addEventListener('input', event => {
      if (event.target.matches('[data-design-search]')) { this.filters.query=event.target.value; this.filter(); }
    });
    this.onHistory = () => this.readURL();
    window.addEventListener('popstate', this.onHistory);
    this.start();
  }

  async start() {
    if (!this.data.automatic) { this.readURL(); return; }
    try {
      const all = await catalogue();
      let context = this.data.context;
      if (context && location.pathname.includes('/collections/')) { const url = new URL(location.href); url.searchParams.set('view','rx-design-data'); ['design','item','tier','garment','variant'].forEach(k=>url.searchParams.delete(k)); context=url.href; }
      this.data.designs = context ? groupsFor(await loadPages(context), all) : all.designs;
      this.data.currency = all.currency;
      this.filters.garment = this.data.garment || '';
      this.root.querySelector('[data-design-fallback]')?.setAttribute('hidden','');
      await this.readURL();
    } catch {
      this.view.innerHTML = '<p role="status">Design choices could not load. Browse the products below or reload this page.</p>';
    }
  }

  money(cents) {
    return new Intl.NumberFormat(this.data.locale || document.documentElement.lang || 'en', { style: 'currency', currency: this.data.currency || 'USD', maximumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
  }

  urlRoot() { return (this.data.root || '/').replace(/\/?$/, '/'); }

  updateURL(replace = false) {
    const url = new URL(location.href);
    ['design', 'garment', 'tier', 'variant', 'item'].forEach(key => url.searchParams.delete(key));
    if (this.state.design) {
      url.searchParams.set('design', this.state.design.id);
      if (this.state.slot) {
        url.searchParams.set('garment', SPECS[this.state.slot.key].garment);
        url.searchParams.set('tier', SPECS[this.state.slot.key].tier);
        url.searchParams.set('item', this.state.slot.handle);
        const variant = this.state.product && matchingVariant(this.state.product, this.state.choices);
        if (variant) url.searchParams.set('variant', variant.id);
      }
    }
    history[replace ? 'replaceState' : 'pushState']({}, '', url);
  }

  async readURL() {
    const params = new URL(location.href).searchParams;
    const design = this.data.designs.find(item => item.id === params.get('design'));
    if (!design) { this.request++; this.state.design = null; this.renderGrid(); return; }
    const slot = design.slots.find(item => item.handle === params.get('item')) || design.slots.find(item => SPECS[item.key]?.garment === params.get('garment') && SPECS[item.key]?.tier === params.get('tier')) || design.slots.find(item => item.handle) || design.slots[0];
    await this.open(design, slot, false, params.get('variant'));
  }

  renderGrid() {
    this.view.innerHTML = `<header class="rx-ds-intro"><p class="rx-ds-eyebrow">THE DESIGN INDEX</p><h1>${escapeHTML(this.data.heading || 'CHOOSE YOUR PRESCRIPTION.')}</h1><p>Find your design. Choose your weight. Same support group.</p></header>
      <div class="rx-ds-toolbar"><span data-design-count aria-live="polite">${this.data.designs.length} DESIGNS</span><label><span class="rx-ds-sr">Search designs</span><input type="search" placeholder="Find a design" value="${escapeHTML(this.filters.query)}" data-design-search></label></div>
      <div class="rx-ds-filters"><div role="group" aria-label="Filter by series">${['','CORE REKT','HOLDER SERIES'].map(value=>`<button type="button" data-filter="series" data-filter-value="${value}" aria-pressed="${this.filters.series===value}">${value || 'All designs'}</button>`).join('')}</div><div role="group" aria-label="Filter by garment">${['','tee','hoodie','jacket'].map(value=>`<button type="button" data-filter="garment" data-filter-value="${value}" aria-pressed="${this.filters.garment===value}">${value ? garmentLabel(value) : 'All garments'}</button>`).join('')}</div></div><div class="rx-ds-grid">${this.data.designs.map(design => {
        const active = design.slots.filter(slot => slot.handle && (!design.contextHandles || design.contextHandles.includes(slot.handle)));
        const hero = design.contextHandles ? active[0]?.image || design.image : design.image;
        const prices = active.map(slot => slot.price).filter(price => Number.isFinite(price));
        const count = active.length;
        return `<article class="rx-ds-card" data-design-card="${escapeHTML(design.id)}"><a href="?design=${encodeURIComponent(design.id)}" data-open="${escapeHTML(design.id)}"><div class="rx-ds-card-image ${design.artwork ? 'rx-ds-artwork' : ''}">${safeImage(hero) ? `<img src="${escapeHTML(safeImage(hero))}" alt="${escapeHTML(design.title)}" loading="lazy" width="700" height="800">` : '<span>REKTWEAR</span>'}<span class="rx-ds-stamp">${active.length ? escapeHTML(design.universe || 'CORE REKT') : 'COMING SOON'}</span><span data-edition-ribbon>${editionRibbon(design,active)}</span></div><div class="rx-ds-card-copy"><h2>${escapeHTML(design.title)}</h2><p>${escapeHTML(choiceSummary(design))}</p><p data-edition-options class="rx-auto-edition-options"></p><div class="rx-ds-card-meta"><span data-card-price>${active.length ? `${count} ${count === 1 ? 'option' : 'options'}${prices.length ? ' · From ' + this.money(Math.min(...prices)) : ''}` : 'Explore the upcoming range'}</span><span aria-hidden="true">↗</span></div></div></a></article>`;
      }).join('')}</div><p class="rx-ds-empty" hidden>No designs match your search.</p>`;
    this.filter();
  }

  filter() {
    let visible = 0;
    this.root.querySelectorAll('[data-design-card]').forEach(card => {
      const design=this.data.designs.find(d=>d.id===card.dataset.designCard);
      card.hidden = !matchesDesign(design,this.filters);
      if(!card.hidden) visible++;
      const slots=design.slots.filter(slot=>slot.handle && (!design.contextHandles || design.contextHandles.includes(slot.handle)) && (!this.filters.garment || SPECS[slot.key].garment===this.filters.garment));
      const prices=slots.map(s=>s.price).filter(Number.isFinite);
      card.querySelector('[data-edition-ribbon]').innerHTML=editionRibbon(design,slots);
      const editionLine=card.querySelector('[data-edition-options]');editionLine.hidden=!slots.some(s=>s.edition);editionLine.textContent=editionLine.hidden?'':'Limited edition: '+editionChoices(slots);
      card.querySelector('[data-card-price]').textContent=slots.length ? `${slots.length} ${slots.length===1?'option':'options'}${prices.length ? ' · From '+this.money(Math.min(...prices)) : ''}` : 'Explore the upcoming range';
    });
    this.root.querySelector('.rx-ds-empty').hidden=visible>0;
    this.root.querySelector('[data-design-count]').textContent=`${visible} OF ${this.data.designs.length} DESIGNS`;
    this.root.querySelectorAll('[data-filter]').forEach(button=>button.setAttribute('aria-pressed',String(this.filters[button.dataset.filter]===button.dataset.filterValue)));
  }

  async open(design, slot, push = true, variantId = null) {
    const previous = design.id === this.state.design?.id ? this.state.choices : {};
    const request = ++this.request;
    this.state = { design, slot, choices: previous, product: null, loading: !!slot.handle, image: design.image, error: '', busy: this.cartBusy };
    if (push) this.updateURL();
    this.renderDetail();
    if (!slot.handle) return;
    try {
      let product = this.cache.get(slot.handle);
      if (!product) {
        const url = this.data.preview ? `./data/${encodeURIComponent(slot.handle)}.json` : `${this.urlRoot()}products/${encodeURIComponent(slot.handle)}.js`;
        const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('This option is temporarily unavailable. Please try another tier.');
        product = await response.json();
        if (!Array.isArray(product.variants) || !product.variants.length || product.variants.length >= 250) throw new Error('Please open the product page to choose this option.');
        this.cache.set(slot.handle, product);
      }
      if (request !== this.request) return;
      this.state.product = product;
      this.state.choices = compatibleChoices(product, previous);
      const variant = variantId && product.variants.find(item => String(item.id) === String(variantId));
      if (variant) this.state.choices = Object.fromEntries(optionNames(product).map((name, index) => [name, variant.options[index]]));
      this.state.image = this.variantImage() || safeImage(product.featured_image) || design.image;
      this.state.loading = false;
      this.renderDetail();
      if (push) this.updateURL(true);
    } catch (error) {
      if (request !== this.request) return;
      this.state.loading = false;
      this.state.error = error.message;
      this.renderDetail();
    }
  }

  variantImage() {
    const { product, choices } = this.state;
    if (!product) return '';
    const names = optionNames(product);
    const colorIndex = names.findIndex(name => /colou?r/i.test(name));
    const variant = matchingVariant(product, choices) || product.variants.find(item => colorIndex < 0 || item.options[colorIndex] === choices[names[colorIndex]]);
    return safeImage(variant?.featured_image);
  }

  renderDetail(focusKey) {
    const { design, slot, product, choices, loading, error, busy } = this.state;
    const spec = SPECS[slot.key];
    const variants = design.slots.filter(item => SPECS[item.key].garment === spec.garment);
    const garments = [...new Set(design.slots.map(item => SPECS[item.key].garment))];
    const selected = product && matchingVariant(product, choices);
    const price = selected?.price ?? product?.price_min ?? slot.price;
    const images = product ? [...new Set([this.state.image, ...(product.images || []).map(safeImage)].filter(Boolean))] : [design.image];
    const actionable = selected?.available && !loading && !busy;
    const productPath = slot.handle ? `${this.urlRoot()}products/${encodeURIComponent(slot.handle)}${selected ? '?variant=' + selected.id : ''}` : '';
    this.view.innerHTML = `<a href="${escapeHTML(location.pathname)}" class="rx-ds-back" data-back>← ALL DESIGNS</a><div class="rx-ds-detail">
      <div class="rx-ds-gallery"><div class="rx-ds-main-image ${!product && design.artwork ? 'rx-ds-artwork' : ''}"><img src="${escapeHTML(safeImage(this.state.image) || safeImage(design.image))}" alt="${escapeHTML(product ? `${design.title} — ${spec.tier} ${spec.garment}` : design.title + ' artwork')}" width="1000" height="1100"><span class="rx-ds-stamp">${product ? escapeHTML(spec.weight + ' / ' + spec.tier) : 'ARTWORK PREVIEW'}</span>${editionRibbon(design,[slot])}</div>
      ${images.length > 1 ? `<div class="rx-ds-thumbnails" aria-label="Product images">${images.slice(0, 16).map((image, index) => `<button type="button" data-image="${escapeHTML(image)}" aria-label="View image ${index + 1}" aria-pressed="${this.state.image === image}"><img src="${escapeHTML(image)}" alt="" width="90" height="90" loading="lazy"></button>`).join('')}</div>` : ''}</div>
      <div class="rx-ds-panel"><p class="rx-ds-eyebrow">${escapeHTML(design.universe || 'CORE REKT')} / REKTWEAR</p><h1 tabindex="-1">${escapeHTML(design.title)}</h1><p class="rx-ds-story">${escapeHTML(design.line || '')}</p>${slot.edition ? `<p class="rx-ds-edition">${escapeHTML(slot.edition)}</p>` : ''}<div class="rx-ds-price" aria-live="polite">${Number.isFinite(price) ? this.money(price) : 'COMING SOON'}</div>
      <fieldset class="rx-ds-garments"><legend>01 / CHOOSE YOUR GARMENT</legend>${garments.map(garment => `<button type="button" data-garment="${garment}" aria-pressed="${spec.garment === garment}">${garmentLabel(garment)}</button>`).join('')}</fieldset>
      <fieldset class="rx-ds-tiers"><legend>02 / CHOOSE YOUR TIER</legend>${variants.map(item => {
        const info = SPECS[item.key];
        return `<button type="button" class="rx-ds-tier" data-tier="${item.handle}" aria-pressed="${item.handle === slot.handle}"><span class="rx-ds-tier-number">${info.tier}</span><span><strong>${escapeHTML(item.edition || info.name)}</strong><small>${escapeHTML(item.spec || `${info.weight} · ${info.fit}`)}</small></span><span class="rx-ds-tier-price">${item.handle && Number.isFinite(item.price) ? this.money(item.price) : 'Soon'}</span></button>`;
      }).join('')}</fieldset><a class="rx-ds-product-link" href="${this.urlRoot()}pages/value-proposition">Which tier is right for me? ↗</a><p class="rx-ds-tier-note">${escapeHTML(spec.note)}</p>
      ${loading ? '<p role="status" class="rx-ds-message">Loading colours and sizes…</p>' : ''}
      ${error ? `<p role="alert" class="rx-ds-message">${escapeHTML(error)}</p>` : ''}
      ${product ? `<form data-buy-form>${optionNames(product).map((name, index) => `<fieldset class="rx-ds-options"><legend>${String(index + 3).padStart(2, '0')} / ${escapeHTML(name.toUpperCase())}${choices[name] ? `<span> — ${escapeHTML(choices[name])}</span>` : ''}</legend><div>${optionValues(product, index).map(value => `<button type="button" data-option="${index}" data-value="${escapeHTML(value)}" aria-pressed="${choices[name] === value}" ${optionAvailable(product, choices, index, value) ? '' : 'disabled'}>${escapeHTML(value)}</button>`).join('')}</div></fieldset>`).join('')}
      <button type="submit" class="rx-ds-buy" ${actionable ? '' : 'disabled'}>${busy ? 'ADDING…' : selected ? (selected.available ? (this.data.preview || slot.edition ? 'VIEW SELECTED ITEM IN STORE ↗' : `ADD TO CART — ${this.money(selected.price)}`) : 'SOLD OUT IN THIS COMBINATION') : 'CHOOSE YOUR SIZE'}</button></form>` : !loading && !error ? '<p class="rx-ds-message">This design is in preparation. Explore the garment options above.</p><button class="rx-ds-buy" disabled>COMING SOON</button>' : ''}
      ${productPath ? `<a class="rx-ds-product-link" href="${escapeHTML(this.data.preview ? 'https://rektwear.co' + productPath : productPath)}">Full product details &amp; size guide ↗</a>` : ''}
      <p class="rx-ds-footnote">${this.data.preview ? 'Preview: prices and availability were captured from the store on 9 September 2026. No items are added to your cart here.' : 'Shipping and applicable taxes are calculated at checkout.'}</p></div></div>`;
    if (focusKey) this.view.querySelector(focusKey)?.focus({ preventScroll: true });
  }

  async click(event) {
    const button = event.target.closest('button, a');
    if (!button || !this.root.contains(button)) return;
    if (this.cartBusy) { event.preventDefault(); return; }
    if (button.hasAttribute('data-filter')) {
      this.filters[button.dataset.filter]=button.dataset.filterValue; this.filter();
    } else if (button.hasAttribute('data-open')) {
      event.preventDefault();
      const design = this.data.designs.find(item => item.id === button.dataset.open);
      await this.open(design, design.slots.find(item => SPECS[item.key].garment===this.filters.garment && item.handle) || design.slots.find(item => design.contextHandles?.includes(item.handle)) || design.slots.find(item => item.handle) || design.slots[0]);
      this.view.querySelector('h1')?.focus({ preventScroll: true });
      this.root.scrollIntoView({ block: 'start' });
    } else if (button.hasAttribute('data-back')) {
      event.preventDefault(); this.request++; this.state.design = null; this.updateURL(); this.renderGrid(); this.view.querySelector('[data-design-search]')?.focus({ preventScroll: true });
    } else if (button.dataset.garment) {
      const slot = this.state.design.slots.find(item => SPECS[item.key].garment === button.dataset.garment && SPECS[item.key].tier === SPECS[this.state.slot.key].tier) || this.state.design.slots.find(item => SPECS[item.key].garment === button.dataset.garment);
      await this.open(this.state.design, slot); this.view.querySelector(`[data-garment="${button.dataset.garment}"]`)?.focus({ preventScroll: true });
    } else if (button.dataset.tier) {
      await this.open(this.state.design, this.state.design.slots.find(item => item.handle === button.dataset.tier)); this.view.querySelector(`[data-tier="${button.dataset.tier}"]`)?.focus({ preventScroll: true });
    } else if (button.hasAttribute('data-option')) {
      const names = optionNames(this.state.product);
      const index = Number(button.dataset.option);
      this.state.choices[names[index]] = button.dataset.value;
      names.slice(index + 1).forEach(name => { if (!optionAvailable(this.state.product, this.state.choices, names.indexOf(name), this.state.choices[name])) delete this.state.choices[name]; });
      this.state.image = this.variantImage() || this.state.image;
      this.renderDetail(`[data-option="${index}"][data-value="${CSS.escape(button.dataset.value)}"]`); this.updateURL(true);
    } else if (button.dataset.image) {
      this.state.image = button.dataset.image; this.renderDetail(`[data-image="${CSS.escape(button.dataset.image)}"]`);
    }
  }

  async submit(event) {
    if (!event.target.matches('[data-buy-form]')) return;
    event.preventDefault();
    const variant = matchingVariant(this.state.product, this.state.choices);
    if (!variant?.available || this.cartBusy) return;
    if (this.state.slot.edition && !this.data.preview) {
      location.assign(`${this.urlRoot()}products/${encodeURIComponent(this.state.product.handle)}?variant=${variant.id}`); return;
    }
    if (this.data.preview) {
      window.open(`https://rektwear.co/products/${encodeURIComponent(this.state.product.handle)}?variant=${variant.id}`, '_blank', 'noopener');
      return;
    }
    this.cartBusy = true; this.state.busy = true; this.state.error = ''; this.renderDetail();
    try {
      const response = await fetch(`${this.urlRoot()}cart/add.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }) });
      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.description || 'This combination is unavailable. Please choose another size or colour.');
      }
      location.assign(`${this.urlRoot()}cart`);
    } catch (error) {
      this.state.error = error instanceof TypeError ? 'The connection was interrupted. Check your cart before trying again.' : error.message;
      this.cartBusy = false; this.state.busy = false;
      if (this.state.design) this.renderDetail();
    }
  }
}

function init(scope = document) {
  scope.querySelectorAll('[data-rx-design-shop]').forEach(root => {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    try { root.designShop = new DesignShop(root); }
    catch { root.querySelector('[data-design-view]').textContent = 'The design index is temporarily unavailable. Please use the store navigation to browse products.'; }
  });
}
if (typeof document !== 'undefined') {
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-rx-design-shop]').forEach(root => window.removeEventListener('popstate', root.designShop?.onHistory)));
}
